import { useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { getApolloContext } from "@apollo/client/react";
import {
  watchRefreshSettle,
  type RefreshQueryLike,
  type RefreshSettleOutcome,
} from "#app/widgets/dashboards/refresh-settle";

// The Auto-refresh cadence: how long since the last refresh RAN (landed or
// not) before the auto path re-runs the board's queries. Keyed on the run, not
// the success, so a failing board keeps the cadence instead of retrying on
// every due-check.
const AUTO_REFRESH_INTERVAL_MS = 5 * 60_000;
// How often the auto path checks whether the cadence is due. Due-ness derives
// from the last run, so a fine-grained check never skews the cadence — it
// only bounds how late a due refresh can fire.
const AUTO_REFRESH_CHECK_MS = 30_000;
// The spinner holds at least this long, so a round-trip that lands in a few
// milliseconds still reads as a refresh rather than a flicker.
const MIN_REFRESHING_MS = 400;
// How long the success mark stays before the control goes quiet again.
const SUCCESS_VISIBLE_MS = 2500;
// A refresh still pending past this settles to idle with no claim either way —
// a query that never lands, or a remount that never reports.
const SETTLE_TIMEOUT_MS = 30_000;

export type WidgetRefreshState = "idle" | "refreshing" | "success" | "failed";

/**
 * The ONE refresh state machine behind every widget refresh affordance — the
 * free toolbar's board-level control (owned by the Dashboard host) and the
 * detail dialog's header action.
 *
 * A refresh bumps `tick`; the caller keys a `WidgetRefreshBoundary` on it, so
 * the subtree inside remounts and every widget body's queries re-run. The
 * boundary's mount effect runs after every body has subscribed, and hands the
 * remount to `onRemounted`, which watches the queries the remount created
 * (`refresh-settle.ts`: the client's active observable queries minus the set
 * snapshotted before the bump) until none is in flight. Only then does the
 * state claim anything: `success` when every query settled clean, `failed`
 * when any settled with an error, back to `idle` with no claim when the watch
 * times out. `success` clears itself after a moment; `failed` holds until the
 * next refresh.
 *
 * `onRefresh` fires on each refresh for the host's side effects (analytics);
 * its `auto` argument says which path fired — false for a press, true for an
 * auto-refresh tick.
 *
 * `auto` arms the auto path: the same refresh on a fixed cadence, gated on
 * the tab being visible — a backgrounded dashboard re-fetching all day is
 * pure waste. A manual press resets the clock, and the visibility listener
 * catches a returning tab up as soon as the last run has aged past the
 * cadence.
 */
export function useWidgetRefresh(
  onRefresh?: (auto: boolean) => void,
  auto = false,
) {
  // Read optionally: the dialog also renders in harnesses with no
  // ApolloProvider (a registry of query-free widgets). Without a client the
  // remount still happens; the refresh just settles with no claim.
  const client = useContext(getApolloContext()).client ?? null;
  const [state, setState] = useState<WidgetRefreshState>("idle");
  const stateRef = useRef<WidgetRefreshState>("idle");
  const [tick, setTick] = useState(0);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(() => Date.now());
  const lastRunAt = useRef(Date.now());
  const startedAt = useRef(0);
  const before = useRef<ReadonlySet<RefreshQueryLike> | null>(null);
  const cancelWatch = useRef<(() => void) | null>(null);
  // One timer slot: the no-remount cap, then the minimum-spin hold, then the
  // success dwell — always sequential, so a single slot is the honest shape.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const transition = useCallback((next: WidgetRefreshState) => {
    stateRef.current = next;
    setState(next);
  }, []);
  const clearPending = useCallback(() => {
    cancelWatch.current?.();
    cancelWatch.current = null;
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const settle = useCallback(
    (outcome: RefreshSettleOutcome) => {
      clearPending();
      const hold = Math.max(0, MIN_REFRESHING_MS - (Date.now() - startedAt.current));
      timer.current = setTimeout(() => {
        timer.current = null;
        if (outcome === "success") {
          setLastRefreshedAt(Date.now());
          transition("success");
          timer.current = setTimeout(() => {
            timer.current = null;
            transition("idle");
          }, SUCCESS_VISIBLE_MS);
          return;
        }
        transition(outcome === "failed" ? "failed" : "idle");
      }, hold);
    },
    [clearPending, transition],
  );

  const run = useCallback(
    (isAuto: boolean) => {
      if (stateRef.current === "refreshing") return;
      clearPending();
      before.current = client ? client.getObservableQueries("active") : null;
      startedAt.current = Date.now();
      lastRunAt.current = startedAt.current;
      transition("refreshing");
      setTick((n) => n + 1);
      onRefresh?.(isAuto);
      // The remount starts the watch; until the boundary reports it, this cap
      // is the only way out of "refreshing".
      timer.current = setTimeout(() => settle("timeout"), SETTLE_TIMEOUT_MS);
    },
    [client, clearPending, onRefresh, settle, transition],
  );
  // Zero-arg so it can sit directly on an onClick without the event leaking
  // into the `auto` slot.
  const refresh = useCallback(() => run(false), [run]);

  // The `WidgetRefreshBoundary` mount effect. Ignores mounts that are not a
  // refresh (the board's first paint, the dialog opening) and a second
  // boundary reporting the same tick — the running watch already picks up
  // whatever that one subscribed.
  const onRemounted = useCallback(() => {
    if (stateRef.current !== "refreshing" || cancelWatch.current !== null) return;
    if (client === null || before.current === null) {
      settle("timeout");
      return;
    }
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    cancelWatch.current = watchRefreshSettle(client, before.current, settle, {
      timeoutMs: SETTLE_TIMEOUT_MS,
    });
  }, [client, settle]);

  useEffect(() => {
    if (!auto) return;
    const tickIfDue = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastRunAt.current < AUTO_REFRESH_INTERVAL_MS) return;
      run(true);
    };
    const id = setInterval(tickIfDue, AUTO_REFRESH_CHECK_MS);
    document.addEventListener("visibilitychange", tickIfDue);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tickIfDue);
    };
  }, [auto, run]);
  useEffect(() => clearPending, [clearPending]);

  return { state, tick, lastRefreshedAt, refresh, onRemounted };
}

export type WidgetRefresh = ReturnType<typeof useWidgetRefresh>;

/**
 * The remount seam a refresh keys on. Wrap the subtree a refresh re-runs and
 * key this on the hook's `tick`: the key remounts everything inside, and the
 * mount effect — which React runs after every descendant's own effects, so
 * after every widget body has subscribed its queries — reports the remount to
 * the hook's `onRemounted`. Renders no element of its own.
 */
export function WidgetRefreshBoundary({
  onMounted,
  children,
}: {
  onMounted: () => void;
  children: ReactNode;
}): ReactNode {
  useEffect(() => {
    onMounted();
  }, [onMounted]);
  return children;
}
